// admin/src/pages/ManageBlogTravel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import adminApi from '../services/adminApi';
import './ManageBlogTravel.css';

const SERVER_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5000';

const resolveUrl = (src) => {
  if (!src) return '';
  if (src.startsWith('http') || src.startsWith('data:')) return src;
  if (src.startsWith('/uploads/')) return `${SERVER_URL}${src}`;
  return src;
};

const TABS = [
  { key: 'config',  label: 'Settings' },
  { key: 'hero',    label: 'Hero Slides' },
  { key: 'top',     label: 'Top Cards' },
  { key: 'bottom',  label: 'Bottom Cards' },
];

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const ManageBlogTravel = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg,   setErrorMsg]   = useState('');

  // Config state
  const [description, setDescription] = useState('');
  const [descDraft,   setDescDraft]   = useState('');
  const [ctaText,     setCtaText]     = useState('');
  const [ctaDraft,    setCtaDraft]    = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  // Modal state
  const [modal, setModal] = useState({ open: false, type: null, section: null, slide: null });

  const showSuccess = (msg) => { setSuccessMsg(msg); setErrorMsg('');  setTimeout(() => setSuccessMsg(''), 3500); };
  const showError   = (msg) => { setErrorMsg(msg);   setSuccessMsg(''); setTimeout(() => setErrorMsg(''),  5000); };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.getBlogTravelAdmin();
      if (res.success) {
        const d = res.data;
        setData(d);
        setDescription(d.description);
        setDescDraft(d.description);
        setCtaText(d.ctaText);
        setCtaDraft(d.ctaText);
      }
    } catch (err) {
      showError('Failed to load blog travel data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Config save ─── */
  const handleSaveConfig = async () => {
    if (!descDraft.trim()) return showError('Description cannot be empty');
    if (!ctaDraft.trim())  return showError('Button text cannot be empty');
    try {
      setConfigSaving(true);
      await adminApi.updateBlogTravelConfig({ description: descDraft.trim(), ctaText: ctaDraft.trim() });
      setDescription(descDraft.trim());
      setCtaText(ctaDraft.trim());
      showSuccess('Settings saved');
    } catch { showError('Failed to save settings'); }
    finally   { setConfigSaving(false); }
  };

  /* ─── Slide actions ─── */
  const openAdd    = (section) => setModal({ open: true, type: 'add',  section, slide: null });
  const openEdit   = (section, slide) => setModal({ open: true, type: 'edit', section, slide });
  const openDelete = (section, slide) => setModal({ open: true, type: 'delete', section, slide });
  const closeModal = () => setModal({ open: false, type: null, section: null, slide: null });

  const handleAdd = async (formData) => {
    try {
      await adminApi.addBlogSlide(modal.section, formData);
      await fetchData();
      closeModal();
      showSuccess('Slide added');
    } catch (err) { showError(err.message || 'Failed to add slide'); }
  };

  const handleUpdate = async (formData) => {
    try {
      await adminApi.updateBlogSlide(modal.section, modal.slide._id, formData);
      await fetchData();
      closeModal();
      showSuccess('Slide updated');
    } catch (err) { showError(err.message || 'Failed to update slide'); }
  };

  const handleDelete = async () => {
    try {
      await adminApi.deleteBlogSlide(modal.section, modal.slide._id);
      await fetchData();
      closeModal();
      showSuccess('Slide deleted');
    } catch { showError('Failed to delete'); }
  };

  const handleToggle = async (section, id) => {
    try {
      await adminApi.toggleBlogSlide(section, id);
      await fetchData();
      showSuccess('Status updated');
    } catch { showError('Failed to toggle'); }
  };

  const handleReorder = async (section, slides, index, direction) => {
    const sorted = [...slides];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= sorted.length) return;
    [sorted[index], sorted[target]] = [sorted[target], sorted[index]];
    try {
      await adminApi.reorderBlogSlides(section, sorted.map((s) => s._id));
      await fetchData();
      showSuccess('Reordered');
    } catch { showError('Failed to reorder'); }
  };

  /* ─── Slide arrays by tab ─── */
  const getSectionSlides = (section) => {
    if (!data) return [];
    const arr = section === 'hero'   ? data.heroSlides
              : section === 'top'    ? data.topCardSlides
              : section === 'bottom' ? data.bottomCardSlides
              : [];
    return [...arr].sort((a, b) => a.order - b.order);
  };

  const getSectionLabel = (section) =>
    section === 'hero' ? 'Hero Slides' : section === 'top' ? 'Top Card Slides' : 'Bottom Card Slides';

  if (loading) {
    return (
      <div className="btm">
        <div className="btm__header"><h1 className="btm__title">Blog Travel Manager</h1></div>
        <div className="btm__loading"><div className="btm__spinner" /><p>Loading…</p></div>
      </div>
    );
  }

  const currentSection = activeTab !== 'config' ? activeTab : null;
  const slides = currentSection ? getSectionSlides(currentSection) : [];
  const activeCount = slides.filter((s) => s.isActive).length;

  return (
    <div className="btm">
      {/* Header */}
      <div className="btm__header">
        <div>
          <h1 className="btm__title">Blog Travel Manager</h1>
          <p className="btm__subtitle">Manage all content in the Blog Travel section of the homepage</p>
        </div>
        {currentSection && (
          <button className="btm__btn btm__btn--primary" onClick={() => openAdd(currentSection)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Slide
          </button>
        )}
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="btm__msg btm__msg--success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="btm__msg btm__msg--error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {errorMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="btm__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`btm__tab ${activeTab === tab.key ? 'btm__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─── CONFIG TAB ─── */}
      {activeTab === 'config' && (
        <div className="btm__config-card">
          <h2 className="btm__section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Section Settings
          </h2>

          <div className="btm__field">
            <label className="btm__label">Section Description</label>
            <p className="btm__hint">The paragraph text that appears next to the "Blog about traveling" heading.</p>
            <textarea
              className="btm__textarea"
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              rows={4}
              maxLength={600}
              placeholder="Enter section description…"
            />
            <span className="btm__char">{descDraft.length}/600</span>
          </div>

          <div className="btm__field">
            <label className="btm__label">CTA Button Text</label>
            <p className="btm__hint">The label on the "Read all articles" button.</p>
            <input
              type="text"
              className="btm__input"
              value={ctaDraft}
              onChange={(e) => setCtaDraft(e.target.value)}
              maxLength={60}
              placeholder="e.g. Read all articles"
            />
          </div>

          <div className="btm__config-footer">
            <button
              className="btm__btn btm__btn--primary"
              onClick={handleSaveConfig}
              disabled={configSaving || (descDraft === description && ctaDraft === ctaText)}
            >
              {configSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      )}

      {/* ─── SLIDE TABS ─── */}
      {currentSection && (
        <>
          <div className="btm__section-info">
            <span>{activeCount} active of {slides.length} slides</span>
            {currentSection === 'hero' && (
              <span className="btm__badge">Supports: Image · Video · GIF</span>
            )}
            {(currentSection === 'top' || currentSection === 'bottom') && (
              <span className="btm__badge">Supports: Image · GIF</span>
            )}
          </div>

          {slides.length === 0 ? (
            <div className="btm__empty">
              <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <h3>No slides yet</h3>
              <p>Add your first slide to the <strong>{getSectionLabel(currentSection)}</strong></p>
              <button className="btm__btn btm__btn--primary" onClick={() => openAdd(currentSection)}>
                Add First Slide
              </button>
            </div>
          ) : (
            <div className="btm__grid">
              {slides.map((slide, index) => (
                <SlideCard
                  key={slide._id}
                  slide={slide}
                  index={index}
                  total={slides.length}
                  section={currentSection}
                  onEdit={()            => openEdit(currentSection, slide)}
                  onDelete={()          => openDelete(currentSection, slide)}
                  onToggle={()          => handleToggle(currentSection, slide._id)}
                  onMoveUp={()          => handleReorder(currentSection, slides, index, 'up')}
                  onMoveDown={()        => handleReorder(currentSection, slides, index, 'down')}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── ADD/EDIT MODAL ─── */}
      {modal.open && modal.type !== 'delete' && (
        <SlideFormModal
          title={modal.type === 'add' ? `Add ${getSectionLabel(modal.section)} Slide` : 'Edit Slide'}
          section={modal.section}
          initialData={modal.slide}
          onClose={closeModal}
          onSave={modal.type === 'add' ? handleAdd : handleUpdate}
        />
      )}

      {/* ─── DELETE MODAL ─── */}
      {modal.open && modal.type === 'delete' && modal.slide && (
        <div className="btm__overlay" onClick={closeModal}>
          <div className="btm__modal btm__modal--sm" onClick={(e) => e.stopPropagation()}>
            <div className="btm__delete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </div>
            <h3 className="btm__delete-title">Delete Slide?</h3>
            <p className="btm__delete-text">
              Delete "<strong>{modal.slide.title}</strong>"? This cannot be undone.
            </p>
            <div className="btm__delete-actions">
              <button className="btm__btn btm__btn--ghost" onClick={closeModal}>Cancel</button>
              <button className="btm__btn btm__btn--danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   SLIDE CARD
═══════════════════════════════════════ */
const SlideCard = ({ slide, index, total, section, onEdit, onDelete, onToggle, onMoveUp, onMoveDown }) => {
  const isVideo = slide.type === 'video';
  const preview = resolveUrl(slide.src);

  return (
    <div className={`btm__card ${!slide.isActive ? 'btm__card--inactive' : ''}`}>
      <div className="btm__card-media">
        {isVideo ? (
          <video
            src={preview}
            poster={resolveUrl(slide.poster)}
            className="btm__card-thumb"
            muted
            preload="metadata"
          />
        ) : (
          <img src={preview} alt={slide.title} className="btm__card-thumb" loading="lazy" />
        )}

        {!slide.isActive && <div className="btm__badge-pill btm__badge-pill--inactive">Inactive</div>}
        {isVideo && <div className="btm__badge-pill btm__badge-pill--type">VIDEO</div>}
        {slide.type === 'gif' && <div className="btm__badge-pill btm__badge-pill--type">GIF</div>}
        <div className="btm__badge-pill btm__badge-pill--order">#{index + 1}</div>

        <div className="btm__card-overlay">
          <button className="btm__card-action" onClick={onEdit} title="Edit">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button className="btm__card-action btm__card-action--danger" onClick={onDelete} title="Delete">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      <div className="btm__card-body">
        <h3 className="btm__card-title">{slide.title}</h3>
        <div className="btm__card-footer">
          <label className="btm__toggle">
            <input type="checkbox" checked={slide.isActive} onChange={onToggle} />
            <span className="btm__toggle-track" />
            <span className="btm__toggle-label">{slide.isActive ? 'Active' : 'Inactive'}</span>
          </label>
          <div className="btm__reorder">
            <button className="btm__reorder-btn" onClick={onMoveUp}   disabled={index === 0}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15" /></svg>
            </button>
            <button className="btm__reorder-btn" onClick={onMoveDown} disabled={index === total - 1}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════
   SLIDE FORM MODAL
═══════════════════════════════════════ */
const ALLOWED_IMAGE = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_VIDEO = ['video/mp4', 'video/webm', 'video/ogg'];

const SlideFormModal = ({ title, section, initialData, onClose, onSave }) => {
  const isHero = section === 'hero';

  const [slideTitle, setSlideTitle]   = useState(initialData?.title || '');
  const [type,       setType]         = useState(initialData?.type  || 'image');
  const [mediaFile,  setMediaFile]    = useState(null);
  const [mediaUrl,   setMediaUrl]     = useState('');
  const [mediaPreview, setMediaPreview] = useState('');
  const [posterFile, setPosterFile]   = useState(null);
  const [posterUrl,  setPosterUrl]    = useState('');
  const [posterPreview, setPosterPreview] = useState('');
  const [isActive,   setIsActive]     = useState(initialData?.isActive !== false);
  const [errors,     setErrors]       = useState({});
  const [saving,     setSaving]       = useState(false);

  const mediaRef  = useRef(null);
  const posterRef = useRef(null);

  // Pre-fill URLs when editing
  useEffect(() => {
    if (initialData?.src) {
      const resolved = resolveUrl(initialData.src);
      setMediaPreview(resolved);
      if (initialData.src.startsWith('http')) setMediaUrl(initialData.src);
    }
    if (initialData?.poster) {
      const resolved = resolveUrl(initialData.poster);
      setPosterPreview(resolved);
      if (initialData.poster.startsWith('http')) setPosterUrl(initialData.poster);
    }
  }, [initialData]);

  const handleMediaFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = type === 'video' ? ALLOWED_VIDEO : ALLOWED_IMAGE;
    if (!allowed.includes(file.type)) {
      return setErrors((p) => ({ ...p, media: `Invalid type. Allowed: ${allowed.map((t) => t.split('/')[1]).join(', ')}` }));
    }
    if (file.size > 100 * 1024 * 1024) return setErrors((p) => ({ ...p, media: 'Max 100 MB' }));
    setErrors((p) => ({ ...p, media: '' }));
    const reader = new FileReader();
    reader.onload = (ev) => { setMediaFile(file); setMediaUrl(''); setMediaPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handlePosterFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!ALLOWED_IMAGE.includes(file.type)) return setErrors((p) => ({ ...p, poster: 'Image files only' }));
    const reader = new FileReader();
    reader.onload = (ev) => { setPosterFile(file); setPosterUrl(''); setPosterPreview(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const clearMedia = () => {
    setMediaFile(null); setMediaUrl(''); setMediaPreview('');
    if (mediaRef.current) mediaRef.current.value = '';
  };
  const clearPoster = () => {
    setPosterFile(null); setPosterUrl(''); setPosterPreview('');
    if (posterRef.current) posterRef.current.value = '';
  };

  const handleSave = async () => {
    const errs = {};
    if (!slideTitle.trim()) errs.title = 'Title is required';
    if (!initialData && !mediaFile && !mediaUrl) errs.media = 'Media file or URL is required';
    if (Object.keys(errs).length > 0) return setErrors(errs);

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title',    slideTitle.trim());
      formData.append('isActive', isActive);

      if (isHero) {
        formData.append('type', type);
        if (mediaFile)       formData.append('heroMedia', mediaFile);
        else if (mediaUrl)   formData.append('srcUrl',    mediaUrl);
        if (posterFile)      formData.append('heroPoster', posterFile);
        else if (posterUrl)  formData.append('posterUrl',  posterUrl);
      } else {
        if (mediaFile)     formData.append('cardMedia', mediaFile);
        else if (mediaUrl) formData.append('srcUrl',    mediaUrl);
      }

      await onSave(formData);
    } catch (err) {
      setErrors({ general: err.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const mediaAccept = type === 'video'
    ? 'video/mp4,video/webm,video/ogg'
    : 'image/jpeg,image/png,image/webp,image/gif';

  return (
    <div className="btm__overlay" onClick={onClose}>
      <div className="btm__modal btm__modal--form" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="btm__modal-header">
          <h2>{title}</h2>
          <button className="btm__modal-close" onClick={onClose}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="btm__modal-body">
          {errors.general && <div className="btm__msg btm__msg--error">{errors.general}</div>}

          {/* Title */}
          <div className="btm__field">
            <label className="btm__label">Slide Title <span className="btm__req">*</span></label>
            <input
              type="text"
              className={`btm__input ${errors.title ? 'btm__input--err' : ''}`}
              value={slideTitle}
              onChange={(e) => { setSlideTitle(e.target.value); setErrors((p) => ({ ...p, title: '' })); }}
              placeholder="e.g. Explore the Azores"
            />
            {errors.title && <span className="btm__error">{errors.title}</span>}
          </div>

          {/* Media type — hero only */}
          {isHero && (
            <div className="btm__field">
              <label className="btm__label">Media Type</label>
              <div className="btm__type-row">
                {['image', 'video', 'gif'].map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`btm__type-btn ${type === t ? 'btm__type-btn--active' : ''}`}
                    onClick={() => { setType(t); clearMedia(); }}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Media upload / URL */}
          <div className="btm__field">
            <label className="btm__label">
              {isHero ? (type === 'video' ? 'Video' : type === 'gif' ? 'GIF' : 'Image') : 'Image'}
              {!initialData && <span className="btm__req"> *</span>}
              <span className="btm__hint-inline">
                {type === 'video' ? ' mp4/webm, max 100MB' : ' jpg/png/webp/gif, max 100MB'}
              </span>
            </label>

            {mediaPreview ? (
              <div className="btm__preview">
                {type === 'video' ? (
                  <video src={mediaPreview} className="btm__preview-media" controls muted />
                ) : (
                  <img src={mediaPreview} alt="preview" className="btm__preview-media" />
                )}
                <button className="btm__preview-remove" onClick={clearMedia}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Remove
                </button>
              </div>
            ) : (
              <div className="btm__upload-zone">
                <input
                  type="file" ref={mediaRef} id="blog-media"
                  accept={mediaAccept}
                  onChange={handleMediaFile}
                  className="btm__file-input"
                />
                <label htmlFor="blog-media" className="btm__upload-label">
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>Click to upload {type === 'video' ? 'video' : type === 'gif' ? 'GIF' : 'image'}</span>
                </label>
              </div>
            )}

            <div className="btm__url-row">
              <span className="btm__or">or paste URL:</span>
              <input
                type="text"
                className="btm__input btm__input--sm"
                value={mediaUrl}
                onChange={(e) => { setMediaUrl(e.target.value); setMediaPreview(e.target.value); setMediaFile(null); setErrors((p) => ({ ...p, media: '' })); }}
                placeholder="https://example.com/media.mp4"
              />
            </div>
            {errors.media && <span className="btm__error">{errors.media}</span>}
          </div>

          {/* Poster — hero video only */}
          {isHero && type === 'video' && (
            <div className="btm__field">
              <label className="btm__label">Video Poster / Thumbnail <span className="btm__hint-inline">(optional)</span></label>
              {posterPreview ? (
                <div className="btm__preview btm__preview--sm">
                  <img src={posterPreview} alt="poster" className="btm__preview-media" />
                  <button className="btm__preview-remove" onClick={clearPoster}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Remove
                  </button>
                </div>
              ) : (
                <div className="btm__upload-zone btm__upload-zone--sm">
                  <input
                    type="file" ref={posterRef} id="blog-poster"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePosterFile}
                    className="btm__file-input"
                  />
                  <label htmlFor="blog-poster" className="btm__upload-label">
                    <span>Upload poster image</span>
                  </label>
                </div>
              )}
              <div className="btm__url-row">
                <span className="btm__or">or paste URL:</span>
                <input
                  type="text"
                  className="btm__input btm__input--sm"
                  value={posterUrl}
                  onChange={(e) => { setPosterUrl(e.target.value); setPosterPreview(e.target.value); setPosterFile(null); }}
                  placeholder="https://example.com/poster.jpg"
                />
              </div>
            </div>
          )}

          {/* Active toggle */}
          <div className="btm__field">
            <label className="btm__toggle">
              <input type="checkbox" checked={isActive} onChange={() => setIsActive(!isActive)} />
              <span className="btm__toggle-track" />
              <span className="btm__toggle-label">{isActive ? 'Active (visible on site)' : 'Inactive (hidden)'}</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="btm__modal-footer">
          <button className="btm__btn btm__btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btm__btn btm__btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : initialData ? 'Save Changes' : 'Add Slide'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageBlogTravel;
